import { action, Action, thunk, Thunk } from 'easy-peasy';
import UserModel from 'Models/User';
import { User } from 'Models/User/@types';
import { TRootStore } from 'Stores';

export interface FeaturedUsersState {
    featuredUsers: User[];
    featuredTotal: number;
    filteredTotal: number; // New field for filtered results count
    loading: boolean;
    pageFilters: {
        term: string;
        page: number;
        sort: string;
    };

    setFeaturedUsers: Action<FeaturedUsersState, User[]>;
    setFeaturedTotal: Action<FeaturedUsersState, number>;
    setFilteredTotal: Action<FeaturedUsersState, number>; // New action
    setLoading: Action<FeaturedUsersState, boolean>;
    setPageFilters: Action<FeaturedUsersState, Partial<FeaturedUsersState['pageFilters']>>;

    fetchFeaturedUsers: Thunk<
        FeaturedUsersState,
        {
            term?: string;
            page?: number;
            sort?: string;
            append?: boolean;
        },
        null,
        TRootStore
    >;

    toggleFeatureUser: Thunk<
        FeaturedUsersState,
        {
            user: User;
            isFeatured: boolean;
        },
        null,
        TRootStore
    >;
}

const FeaturedUsersStore: FeaturedUsersState = {
    featuredUsers: [],
    featuredTotal: 0,
    filteredTotal: 0, // Initialize filtered total
    loading: false,
    pageFilters: {
        term: '',
        page: 0,
        sort: 'featured_desc',
    },

    setFeaturedUsers: action((state, payload) => {
        state.featuredUsers = payload;
    }),

    setFeaturedTotal: action((state, payload) => {
        state.featuredTotal = payload;
    }),

    setFilteredTotal: action((state, payload) => {
        state.filteredTotal = payload;
    }),

    setLoading: action((state, payload) => {
        state.loading = payload;
    }),

    setPageFilters: action((state, payload) => {
        state.pageFilters = { ...state.pageFilters, ...payload };
    }),

    fetchFeaturedUsers: thunk(async (actions, params, { getState }) => {
        const { term = '', page = 0, sort = 'featured_desc', append = false } = params;
        const state = getState();

        actions.setLoading(true);

        try {
            const res = await UserModel.getFeaturedUsers({
                limit: 20,
                skip: page * 20,
                term: term.trim(),
                fields: ['id', 'slug', 'firstName', 'lastName', 'email', 'featured', '_profileImages'],
            });

            const users = res.results.map((r: any) => r.hit || r);

            if (page === 0 || !append) {
                actions.setFeaturedUsers(users);
            } else {
                actions.setFeaturedUsers([...state.featuredUsers, ...users]);
            }

            // Update filtered total (what the API returned for this search)
            actions.setFilteredTotal(res.totalCount);

            // Only update the actual featured total if we're not searching
            // This ensures the sidebar count remains stable
            if (!term.trim()) {
                actions.setFeaturedTotal(res.totalCount);
            }

            actions.setPageFilters({ term, page, sort });
        } catch (error) {
            console.error('Error fetching featured users:', error);
        } finally {
            actions.setLoading(false);
        }
    }),

    toggleFeatureUser: thunk(async (actions, { user, isFeatured }, { getState }) => {
        const state = getState();

        try {
            await UserModel.toggleFeatureUser(user.id, isFeatured);

            // Update the list immediately for better UX
            if (isFeatured) {
                // When featuring, add the user to the list (if not already there)
                const updatedUsers = [...state.featuredUsers];
                const existingIndex = updatedUsers.findIndex((u) => u.id === user.id);
                if (existingIndex === -1) {
                    updatedUsers.unshift({ ...user, isFeatured });
                    actions.setFeaturedUsers(updatedUsers);
                    actions.setFeaturedTotal(state.featuredTotal + 1);
                }
            } else {
                // When unfeaturing, remove the user from the list
                const updatedUsers = state.featuredUsers.filter((u) => u.id !== user.id);
                actions.setFeaturedUsers(updatedUsers);
                actions.setFeaturedTotal(state.featuredTotal - 1);
            }
        } catch (error) {
            console.error('Error toggling featured user:', error);
            throw error;
        }
    }),
};

export default FeaturedUsersStore;
